import React from "react";
import { CircleNotch, Check, MagicWand } from "@phosphor-icons/react";

const FIELDS = [
  ["audience", "Audience", "Who's on camera — demographics, energy, vibe"],
  ["character_appearance", "Character look", "Hair, makeup, features — keep the SAME identity across shots"],
  ["wardrobe", "Wardrobe", "Outfit palette & styles that vary per shot"],
  ["environment", "Environments", "Settings/locations the story moves through"],
  ["lighting_grade", "Lighting & grade", "Light quality + color grade"],
  ["props", "Props & accessories", "Jewelry, bags, glasses, signature props"],
  ["visual_direction", "Visual direction", "Overall aesthetic & how shots should escalate"],
];

/**
 * Persisted, editable per-series visual direction. Selecting a preset shows
 * its values as placeholders — leave a field blank to use the preset default,
 * type to override. Used both before storyboard generation and inside the
 * storyboard editor.
 */
export const LookFeelPanel = ({ lookFeel, presets, onChange, onSave, saving, compact }) => {
  const lf = lookFeel || {};
  const presetMap = React.useMemo(() => {
    const m = {};
    (presets || []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [presets]);
  const activePreset = presetMap[lf.visual_preset] || {};

  return (
    <div className="rounded-xl border border-purple-200 bg-purple-50/50 p-4 space-y-3" data-testid="lookfeel-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <MagicWand size={16} weight="fill" className="text-purple-600" />
          <span className="text-sm font-bold text-zinc-900">Look &amp; Feel</span>
        </div>
        <button
          onClick={onSave}
          disabled={saving}
          className="text-xs px-3 py-1.5 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1.5"
          data-testid="lookfeel-save-btn"
        >
          {saving ? <><CircleNotch size={13} className="animate-spin" /> Saving</> : <><Check size={13} weight="bold" /> Save Look</>}
        </button>
      </div>
      <p className="text-[11px] text-zinc-500 -mt-1">
        Applied to every cinematic shot. Pick a preset, then tweak any field — blanks fall back to the preset.
      </p>

      <div>
        <label className="text-[11px] font-semibold text-zinc-700 block mb-1">Preset</label>
        <select
          value={lf.visual_preset || ""}
          onChange={(e) => onChange("visual_preset", e.target.value)}
          className="w-full text-xs border border-zinc-200 rounded-lg px-2 py-1.5 bg-white"
          data-testid="lookfeel-preset"
        >
          <option value="">No preset (custom)</option>
          {(presets || []).map((p) => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>
      </div>

      <div className={compact ? "grid grid-cols-1 gap-2.5" : "grid grid-cols-1 sm:grid-cols-2 gap-2.5"}>
        {FIELDS.map(([key, label, hint]) => (
          <div key={key}>
            <label className="text-[11px] font-semibold text-zinc-700 block mb-0.5">{label}</label>
            <textarea
              value={lf[key] || ""}
              onChange={(e) => onChange(key, e.target.value)}
              placeholder={activePreset[key] || hint}
              rows={compact ? 2 : 2}
              className="w-full text-[11px] border border-zinc-200 rounded-lg px-2 py-1.5 resize-none bg-white placeholder:text-zinc-400"
              data-testid={`lookfeel-${key}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default LookFeelPanel;
